import {ISSUE_TYPE_PUBLIC_HOLIDAY, ISSUE_TYPE_VACATION, ROOT_TYPE_UIDS} from "../../constants";
import {IssueType} from "../entities/issue-type.entity";

/**
 * Returns the ShortId of the root an issue type belongs to: the type itself for a root,
 * its parent for a child, null otherwise. Synchronous on purpose: the render sites that call it
 * are synchronous, so it reads the parent's ShortId from the type and never loads the parent.
 */
export function rootUidOf(type: IssueType | null | undefined): string | null {
    if (!type) {
        return null;
    }
    if (type.parentUid) {
        return type.parentUid;
    }
    return ROOT_TYPE_UIDS.includes(type.uid) ? type.uid : null;
}

/**
 * Whether an issue type is the given root or one of its children (behaviour follows the root, D3).
 */
export function belongsToRoot(type: IssueType | null | undefined, rootUid: string): boolean {
    return rootUidOf(type) === rootUid;
}

/**
 * The types an issue of the given root may have: the root itself when it is live and not abstract,
 * then its live children by name. Menu, create actions and the dialog's type picker all use this one
 * rule, so they cannot disagree. Pure: the caller passes the type list (normally the preloaded cache).
 */
export function offerableTypesOf(rootUid: string, types: readonly IssueType[]): IssueType[] {
    const root = types.find(type => type.uid === rootUid && !type.isDeleted && !type.isAbstract);
    const children = types
        .filter(type => type.parentUid === rootUid && !type.isDeleted)
        .sort((a, b) => a.description.localeCompare(b.description));
    return root ? [root, ...children] : children;
}

/**
 * The type a new issue of the given root starts with: the root itself while it is live and not
 * abstract; else its only live child; else the (abstract) root as a placeholder, and the dialog then
 * requires a child. Never empty, because an issue derives its root only from its type. Null only when
 * the root is not in the list (not loaded yet), so the caller looks the root up itself.
 */
export function initialTypeFor(rootUid: string, types: readonly IssueType[]): IssueType | null {
    const offered = offerableTypesOf(rootUid, types);
    if (offered[0]?.uid === rootUid) {
        return offered[0];
    }
    if (offered.length === 1) {
        return offered[0];
    }
    return types.find(type => type.uid === rootUid) ?? null;
}

/**
 * Whether the type list already holds the given root (live or deleted). Before the preload after
 * login has filled the cache the list is empty, and offerableTypesOf() would wrongly say "nothing".
 */
export function isTypeListLoadedFor(rootUid: string, types: readonly IssueType[]): boolean {
    return types.some(type => type.uid === rootUid);
}

/**
 * Whether a root is one of the two absence roots (Absence, Public holiday): their issues record time
 * off, so the dialog hides status, board, labels and description for them and their children.
 */
export function isAbsenceFamily(rootUid: string | null | undefined): boolean {
    return rootUid === ISSUE_TYPE_VACATION || rootUid === ISSUE_TYPE_PUBLIC_HOLIDAY;
}

/**
 * Whether an issue type passes a type filter: an exact match, or a child of the root the filter
 * names (a filter on a root includes its children, D4, as the backend does). A filter naming a
 * child matches that child only, because rootUidOf() never returns a child's uid.
 */
export function matchesTypeFilter(type: IssueType | null | undefined, filterUid: string): boolean {
    return type?.uid === filterUid || belongsToRoot(type, filterUid);
}

/**
 * After an accepted create or update (a move included), a type takes over its root's isStatusless, as the backend copies it
 * (D3); the loaded entity then gives clones and the dialog the right status before a page reload. The
 * root is read through the given lookup; a failed read leaves the flag as it was.
 */
export async function takeOverRootFlags(type: IssueType, findRoot: (uid: string) => Promise<IssueType | null>): Promise<void> {
    if (!type.parentUid) {
        return;
    }
    const root = await findRoot(type.parentUid).catch(() => null);
    if (root) {
        type.isStatusless = root.isStatusless;
    }
}
